import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import api from "../api";

const Page = styled.div`
  background: #f8fafc;
  min-height: 100vh;
  padding: 16px;
  color: #0f172a;
`;
const Title = styled.h2`
  margin: 0 0 8px;
  font-size: 1.6rem;
  font-weight: 800;
`;
const Desc = styled.p`
  margin: 0 0 12px;
  color: #64748b;
  font-size: 0.9rem;
`;
const Card = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.08);
  padding: 14px;
`;
const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;
const Table = styled.table`
  width: 100%;
  min-width: 980px;
  border-collapse: collapse;
  th,
  td {
    border-bottom: 1px solid #e2e8f0;
    padding: 12px 10px;
    text-align: left;
    color: #0f172a;
    vertical-align: middle;
  }
  th {
    color: #64748b;
    font-size: 0.76rem;
    text-transform: uppercase;
  }
`;
const Input = styled.input`
  width: 110px;
  min-height: 40px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 8px 10px;
  color: #0f172a;
  background: #fff;
`;
const Btn = styled.button`
  min-height: 40px;
  border: none;
  border-radius: 10px;
  padding: 0 12px;
  font-weight: 700;
  cursor: pointer;
  background: ${(p) => (p.$secondary ? "#e2e8f0" : "#2563eb")};
  color: ${(p) => (p.$secondary ? "#0f172a" : "#fff")};
`;
const Toast = styled.div`
  margin-bottom: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${(p) => (p.$error ? "#fecaca" : "#bbf7d0")};
  background: ${(p) => (p.$error ? "#fef2f2" : "#f0fdf4")};
  color: ${(p) => (p.$error ? "#b91c1c" : "#166534")};
  font-weight: 700;
  font-size: 0.86rem;
`;
const Hint = styled.div`
  margin-top: 10px;
  color: #64748b;
  font-size: 0.84rem;
`;

const inferCategory = (name = "", code = "") => {
  const raw = `${name} ${code}`.toLowerCase();
  if (raw.includes("ring")) return "Ring";
  if (raw.includes("chain")) return "Chain";
  if (raw.includes("necklace") || raw.includes("complex") || raw.includes("stone")) return "Complex";
  return "General";
};

const recommendedDefaultWeight = (category) => {
  if (category === "Ring") return 1.0;
  if (category === "Chain") return 2.0;
  if (category === "Complex") return 3.0;
  return 1.0;
};

export default function DesignComplexitySettings() {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [toast, setToast] = useState({ text: "", error: false });

  const load = async () => {
    setLoading(true);
    setToast({ text: "", error: false });
    try {
      const res = await api.get("/admin/design-complexity");
      const data = res?.data?.data || [];
      setRows(data);
      setDraft(
        data.reduce((acc, row) => {
          acc[row.design_id] = String(row.complexity_weight ?? 1.0);
          return acc;
        }, {})
      );
    } catch (err) {
      setToast({
        text: err?.response?.data?.message || "Failed to load design complexity settings.",
        error: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const enrichedRows = useMemo(
    () =>
      rows.map((row) => {
        const category = inferCategory(row.design_name, row.design_code);
        return {
          ...row,
          category,
          default_weight: recommendedDefaultWeight(category),
        };
      }),
    [rows]
  );

  const saveRow = async (row) => {
    const value = Number(draft[row.design_id] || 1.0);
    if (Number.isNaN(value) || value < 0.5 || value > 5.0) {
      setToast({ text: "Current Weight must be between 0.5 and 5.0.", error: true });
      return;
    }
    setSavingId(row.design_id);
    setToast({ text: "", error: false });
    try {
      await api.put(`/admin/design-complexity/${row.design_id}`, { complexity_weight: value });
      setToast({ text: `${row.design_name || row.design_code} updated successfully.`, error: false });
      await load();
    } catch (err) {
      setToast({
        text: err?.response?.data?.message || "Failed to save complexity weight.",
        error: true,
      });
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Page>
      <Title>Design Complexity Weights</Title>
      <Desc>These weights affect worker ratings. Higher weight = more credit.</Desc>
      {toast.text ? <Toast $error={toast.error}>{toast.text}</Toast> : null}

      <Card>
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Design Name</th>
                <th>Category</th>
                <th>Default Weight</th>
                <th>Current Weight</th>
                <th>Save</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} style={{ color: "#64748b", textAlign: "center" }}>
                    Loading...
                  </td>
                </tr>
              ) : enrichedRows.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ color: "#64748b", textAlign: "center" }}>
                    No designs found.
                  </td>
                </tr>
              ) : (
                enrichedRows.map((row) => (
                  <tr key={row.design_id}>
                    <td>
                      <div style={{ fontWeight: 700 }}>{row.design_name || "-"}</div>
                      <div style={{ color: "#64748b", fontSize: "0.78rem" }}>{row.design_code || "-"}</div>
                    </td>
                    <td>{row.category}</td>
                    <td>{row.default_weight.toFixed(1)}</td>
                    <td>
                      <Input
                        type="number"
                        step="0.1"
                        min="0.5"
                        max="5.0"
                        value={draft[row.design_id] || ""}
                        onChange={(e) =>
                          setDraft((prev) => ({
                            ...prev,
                            [row.design_id]: e.target.value,
                          }))
                        }
                      />
                    </td>
                    <td>
                      <Btn onClick={() => saveRow(row)} disabled={savingId === row.design_id}>
                        {savingId === row.design_id ? "Saving..." : "Save"}
                      </Btn>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </TableWrap>
        <Hint>Reference: Ring=1.0, Chain=2.0, Complex=3.0 (recommended).</Hint>
      </Card>
    </Page>
  );
}
